<div class="scorecard-container" #scorecardRoot [class.pdf-export]="exporting" [class.full-screen]="fullScreen">
    <!-- data-tour names the parts the model introduction steps through -->
    <header class="sc-header" data-pdf-block data-tour="overview">
        <div class="brandrow">
            <div class="logo">LINQ</div>
            <div class="statebadge" [class.badge-future]="state === 'future'" [title]="(scorecard?.scopeName || 'Scorecard') + ' · ' + (scorecard?.workspaceName || workspaceName)">{{scorecard?.scopeName || 'Scorecard'}} &middot; {{scorecard?.workspaceName || workspaceName}}</div>
        </div>
        <div class="htitle">Economic Process Modelling &ndash; Scorecard</div>
        <div class="hsub">Where economic value is created and destroyed in the models shown. Revenue, cost, friction and capacity are read directly from the model
            <span class="gtag g-grounded">grounded</span>. Risk, option and information value are inferred from the shape of the graph
            <span class="gtag g-ind">indicative</span>. Where the model has no data it says so, rather than inventing a figure.</div>
        @if (exporting) {
            <div class="hsub exported">Exported {{exportDate}}</div>
        }
    </header>

    @if (scorecard) {
        <div class="wrap">
            <div class="controls">
                <div class="ctl">
                    <label id="state-label">State</label>
                    <div class="toggle" role="group" aria-labelledby="state-label">
                        <button type="button" [class.active]="state === 'current'" (click)="showState('current')" [title]="current.workspaceName">{{current.workspaceName}}</button>
                        <button type="button" [class.active]="state === 'future'" [disabled]="!future" (click)="showState('future')">Future state</button>
                    </div>
                </div>
                <div class="ctl">
                    <label id="future-label">Future state (project)</label>
                    @if (projects.length) {
                        <select class="project-select" aria-labelledby="future-label" [value]="futureProject?._key || ''" (change)="selectFuture(projectByKey($any($event.target).value))">
                            <option value="" disabled>Choose a project</option>
                            @for (p of projects; track p._key) {
                                <option [value]="p._key">{{p.name}}</option>
                            }
                        </select>
                    }@else {
                        <span class="controls-hint">No projects to compare with</span>
                    }
                </div>
                @if (reference) {
                    <div class="ctl">
                        <label id="change-label">Show change as</label>
                        <div class="toggle small" role="group" aria-labelledby="change-label">
                            <button type="button" [class.active]="changeMode === 'both'" (click)="changeMode = 'both'">Both</button>
                            <button type="button" [class.active]="changeMode === 'absolute'" (click)="changeMode = 'absolute'">Absolute</button>
                            <button type="button" [class.active]="changeMode === 'percent'" (click)="changeMode = 'percent'">Percent</button>
                        </div>
                    </div>
                }
                <div class="ctl export-ctl">
                    <button mat-stroked-button color="accent" type="button" (click)="exportPdf()" [disabled]="exporting || loadingFuture" data-testid="export-scorecard-pdf">
                        <mat-icon>picture_as_pdf</mat-icon>{{exporting ? 'Exporting…' : 'Export PDF'}}
                    </button>
                    @if (exportError) {
                        <div class="export-error">{{exportError}}</div>
                    }
                </div>
                @if (!exporting) {
                    <div class="ctl">
                        <button mat-stroked-button color="accent" type="button" (click)="toggleFullScreen()" data-testid="scorecard-full-screen"
                            [title]="fullScreen ? 'Leave full screen (Esc)' : 'Show the scorecard full screen'">
                            <mat-icon>{{fullScreen ? 'fullscreen_exit' : 'fullscreen'}}</mat-icon>{{fullScreen ? 'Exit full screen' : 'Full screen'}}
                        </button>
                    </div>
                }
                <div class="controls-hint">
                    @if (loadingFuture) {
                        Loading {{futureProject?.name}}&hellip;
                    }@else if (futureError) {
                        <span class="error">{{futureError}}</span>
                    }@else if (state === 'future') {
                        Each figure shows its change from {{current.workspaceName.toLowerCase()}}. Green is an improvement, red a decline.
                    }@else if (baseline) {
                        Each figure shows its change from the current state. Green is an improvement, red a decline.
                    }@else {
                        {{current.workspaceName}} is the diagnosis of what exists today. Choose a project to see its modelled impact on each dimension.
                    }
                </div>
            </div>

            @if (state === 'current' && baseline) {
                <div class="callout future" data-pdf-block [innerHTML]="baselineSummary"></div>
            }
            @if (state === 'future' && future) {
                <div class="callout future" data-pdf-block [innerHTML]="comparisonSummary"></div>
            }

            <div class="callout" data-pdf-block data-tour="summary" [innerHTML]="scorecard.summary"></div>

            @if (scorecard.bottleneck; as bn) {
                <div class="bottleneck" data-pdf-block data-tour="capacity" [class.bn-bad]="bn.overloaded" [class.bn-ok]="!bn.overloaded">
                    <div class="gauge" [style]="gaugeStyle(bn.load)"><span>{{bn.load}}%</span></div>
                    <div class="bt">
                        <h4>{{bn.overloaded ? 'Capacity issue' : 'Capacity'}}: {{bn.roleName}} <span class="gtag g-grounded">grounded</span></h4>
                        <p [innerHTML]="bn.text"></p>
                        <div class="note">Capacity is the role's people &times; their modelled hours. Demand is each of its actions' duration &times; annual volume, across all models.</div>
                    </div>
                </div>
            }

            @if (dimensionSummary; as d) {
            <section data-pdf-block data-testid="scorecard-dimension">
            <div class="sectlabel"><span class="pill">Heatmap</span> {{d.dimension}}</div>
            <div class="card">
                @if (d.total) {
                    <div class="dim-total">{{d.total}} <span>across the steps shown</span></div>
                }
                @for (s of d.steps; track $index) {
                    <button type="button" class="dim-row" (click)="stepSelected.emit(s.key)" [attr.aria-label]="'Show ' + s.name + ' in the graph'">
                        <span class="dim-name">{{s.name}}</span>
                        <span class="dim-bar"><i [style.width.%]="s.share * 100" [style.background]="s.colour"></i></span>
                        <span class="dim-value">{{s.label}}</span>
                    </button>
                } @empty {
                    <div class="sub">No steps have any {{d.dimension.toLowerCase()}} in the models shown.</div>
                }
                @if (d.steps.length) {
                    <div class="note">The {{d.steps.length === 1 ? 'step' : d.steps.length + ' steps'}} with the most {{d.dimension.toLowerCase()}}, coloured as in the heatmap. Click one to show it in the graph, or hover over any step there for its value.</div>
                }
            </div>
            </section>
            }

            <section data-pdf-block data-tour="headlines">
            <div class="sectlabel">Headlines</div>
            <div class="card headline-card">
                @for (h of scorecard.headlines; track h.label) {
                    <div class="block">
                        <div class="sub">{{h.label}} <span class="gtag" [class.g-grounded]="h.grounding === 'grounded'" [class.g-ind]="h.grounding === 'indicative'">{{h.grounding}}</span></div>
                        <div class="big" [class]="'tone-' + h.tone">{{h.value}}<ng-container *ngTemplateOutlet="chip; context: {$implicit: headlineChange(h.label, h.measure)}"></ng-container></div>
                        <div class="fromto">{{h.detail}}</div>
                    </div>
                }
            </div>
            </section>

            <section data-pdf-block data-tour="profit-and-loss">
            <div class="sectlabel"><span class="pill">Flow this year</span> Economic P&amp;L &middot; derived from the model</div>
            <div class="grid g3">
                @for (p of scorecard.profitAndLoss; track p.title) {
                    <ng-container *ngTemplateOutlet="panel; context: {$implicit: p, section: 'profitAndLoss'}"></ng-container>
                }
            </div>
            </section>

            <section data-pdf-block data-tour="balance-sheet">
            <div class="sectlabel"><span class="pill bs">Position &amp; exposure</span> Economic Balance Sheet &middot; in each dimension's own units</div>
            <div class="grid g3">
                @for (p of scorecard.balanceSheet; track p.title) {
                    <ng-container *ngTemplateOutlet="panel; context: {$implicit: p, section: 'balanceSheet'}"></ng-container>
                }
            </div>
            </section>

            <section data-pdf-block data-tour="ledger">
            <div class="sectlabel">Value vs Drag ledger &middot; a balanced picture, not a single score</div>
            <div class="ledger">
                <div class="col value">
                    <h4>Value created</h4>
                    @for (l of scorecard.ledger.value; track l.label) {
                        <div class="lrow"><span>{{l.label}}</span><b>{{l.value}}<ng-container *ngTemplateOutlet="chip; context: {$implicit: ledgerChange('value', l.label, l.measure)}"></ng-container></b></div>
                    }
                </div>
                <div class="col drag">
                    <h4>Value drag</h4>
                    @for (l of scorecard.ledger.drag; track l.label) {
                        <div class="lrow"><span>{{l.label}}</span><b>{{l.value}}<ng-container *ngTemplateOutlet="chip; context: {$implicit: ledgerChange('drag', l.label, l.measure)}"></ng-container></b></div>
                    }
                </div>
            </div>

            </section>

            @if (scorecard.valueProfile.length) {
            @let chart = valueProfileChart(scorecard.valueProfile);
            <section data-pdf-block data-testid="value-profile" data-tour="value-profile">
            <div class="sectlabel">Economic Value Profile &middot; what each model contributes</div>
            <div class="card value-profile">
                <div class="vp-legend">
                    <span><i class="vp-swatch vp-created"></i><b>Created</b> above the line</span>
                    <span><i class="vp-swatch vp-destroyed"></i><b>Destroyed</b> below the line</span>
                    <span><i class="vp-swatch vp-unrealised"></i><b>Unrealised</b> hatched cap</span>
                </div>
                <div class="vp-chart">
                    @for (c of chart.columns; track c.bar.modelKey) {
                    <div class="vp-col" [title]="valueProfileTip(c.bar)" role="img" [attr.aria-label]="valueProfileTip(c.bar)">
                        <div class="vp-plot">
                            <span class="vp-line" [style.top.%]="chart.above * 100"></span>
                            @if (c.unrealised) {<i class="vp-unrealised" [style.top.%]="(chart.above - c.created - c.unrealised) * 100" [style.height.%]="c.unrealised * 100"></i>}
                            @if (c.created) {<i class="vp-created" [style.top.%]="(chart.above - c.created) * 100" [style.height.%]="c.created * 100"></i>}
                            @if (c.destroyed) {<i class="vp-destroyed" [style.top.%]="chart.above * 100" [style.height.%]="c.destroyed * 100"></i>}
                        </div>
                        <div class="vp-name">{{c.bar.name}}</div>
                        <div class="vp-amount" [class.vp-negative]="c.bar.net < 0">{{valueProfileAmount(c.bar)}}</div>
                        @if (c.bar.label) {<div class="vp-label" [class.vp-label-unrealised]="c.bar.label === 'Unrealised'">{{c.bar.label}}</div>}
                    </div>
                    }
                </div>
                <div class="note">Each model's revenue and value less the cost of the people and systems doing its work, a year. Revenue is money; where a money value isn't practical, the value set on outputs is the measure, counted one for one with it. Value a model receives from the other models shown is counted in theirs, so no value is counted twice.
                    <b>Unrealised</b> is the cost of producing outputs that are used but that no value reaches: the least they are worth until their value is set.
                    <b>Misaligned</b> models make nothing that carries value or is used; <b>value drag</b> models create value, but less than they cost. Hover over a bar for its figures.</div>
            </div>
            </section>
            }

            @if (!exporting) {
                <section data-tour="lenses">
                <div class="sectlabel">Drill in &middot; the ranked contributors an analyst would act on first</div>
                <div class="tabs" role="tablist">
                    <button type="button" role="tab" [class.active]="lens === 'friction'" [attr.aria-selected]="lens === 'friction'" (click)="lens = 'friction'">Friction lens</button>
                    <button type="button" role="tab" [class.active]="lens === 'risk'" [attr.aria-selected]="lens === 'risk'" (click)="lens = 'risk'">Risk lens</button>
                    <button type="button" role="tab" [class.active]="lens === 'information'" [attr.aria-selected]="lens === 'information'" (click)="lens = 'information'">Information lens</button>
                </div>
                <div class="card table-card">
                    @if (correcting || correctionError) {
                        <div class="decision-source correction-status" role="status" data-testid="correction-status">
                            @if (correcting) {<span>Saving the change and rescoring&hellip;</span>}
                            @if (correctionError) {<div class="error" role="alert">{{correctionError}}</div>}
                        </div>
                    }
                    @switch (lens) {
                        @case ('friction') { <ng-container *ngTemplateOutlet="frictionLens"></ng-container> }
                        @case ('risk') { <ng-container *ngTemplateOutlet="riskLens"></ng-container> }
                        @case ('information') { <ng-container *ngTemplateOutlet="informationLens"></ng-container> }
                    }
                </div>
                </section>
            }@else {
                <section data-pdf-block>
                    <div class="sectlabel">Drill in &middot; friction lens</div>
                    <div class="card table-card"><ng-container *ngTemplateOutlet="frictionLens"></ng-container></div>
                </section>
                <section data-pdf-block>
                    <div class="sectlabel">Drill in &middot; risk lens</div>
                    <div class="card table-card"><ng-container *ngTemplateOutlet="riskLens"></ng-container></div>
                </section>
                <section data-pdf-block>
                    <div class="sectlabel">Drill in &middot; information lens</div>
                    <div class="card table-card"><ng-container *ngTemplateOutlet="informationLens"></ng-container></div>
                </section>
            }

            <section data-pdf-block>
            <div class="sectlabel"><span class="pill dc">Decide</span> Decisions this scorecard supports &middot; prioritised and sequenced</div>
            <div class="decision-source" role="status">
                @switch (aiState) {
                    @case ('loading') {
                        <span class="source-tag">Generating decisions with AI&hellip;</span>
                        <span>The rule-based decisions are shown until they're ready.</span>
                    }
                    @case ('ready') {
                        <span class="source-tag ai">Generated by AI</span>
                        <span>From the figures and signals in this scorecard. Nodes the model doesn't contain are removed.</span>
                        @if (!readOnly) {<button type="button" class="link-button" (click)="generateDecisions(true)">Regenerate</button>}
                    }
                    @case ('failed') {
                        <span class="source-tag">Rule-based</span>
                        <span>AI decisions couldn't be generated, so these come from the scorecard's rules.</span>
                        @if (!readOnly) {<button type="button" class="link-button" (click)="generateDecisions(true)">Try again</button>}
                    }
                    @default {
                        <span class="source-tag">Rule-based</span>
                        @if (!readOnly) {<button type="button" class="link-button" (click)="generateDecisions()">Generate with AI</button>}
                    }
                }
            </div>
            </section>
            @if (decisions.length) {
                <section data-pdf-block data-tour="decisions">
                <div class="dintro">
                    @if (state === 'future') {
                        These are the {{decisionSourceLabel}} for {{current.workspaceName.toLowerCase()}}, checked against {{future.workspaceName}}.
                        <b>Resolved</b> means every signal a decision addresses is gone in the future state, <b>improved</b> that some are.
                        @if (statusCounts; as counts) {
                            <div class="status-summary" data-testid="decision-status-summary">
                                <span class="status status-resolved">&#10003; {{counts.resolved}} resolved</span>
                                <span class="status status-improved">{{counts.improved}} improved</span>
                                <span class="status">{{counts.open}} still open</span>
                                @if (counts.untracked) {
                                    <span class="status status-untracked">{{counts.untracked}} not tracked</span>
                                }
                            </div>
                        }
                    }@else {
                        Each decision is generated from a signal in the model and tied to the nodes that produce it.
                    }
                    <div class="formula">Priority score = <code>Impact</code> &times; <code>Confidence</code> &divide; <code>Effort</code>, scaled to 100. Impact is the share of the process a decision moves: of its people time, or of its revenue as an expected loss for risk decisions. A gate runs before the decisions that depend on it.</div>
                </div>
                <div class="roadmap">
                    @for (d of decisions; track $index) {
                        <div class="rmstep" [class.gate]="d.gate" [class.resolved]="state === 'future' && status(d) === 'resolved'">
                            <span class="snum">{{d.badge || (d.gate ? 'G' : d.sequence)}}</span>
                            <div class="rmt">{{d.title}}</div>
                            <div class="rmp">{{d.gate ? 'Gate' : 'Priority ' + d.priority}}@if (state === 'future' && statusText(d); as st) { &middot; {{st}} }</div>
                        </div>
                    }
                </div>
            </section>
                <div class="dgrid">
                    @for (d of decisions; track $index) {
                        <div class="dcard" data-pdf-block [class.gatecard]="d.gate">
                            <div class="top">
                                <div class="drow">
                                    <div class="seqbadge">{{d.badge || (d.gate ? 'G' : d.sequence)}}</div>
                                    <div class="dmain">
                                        <span class="cat" [class]="'cat cat-' + d.lever">{{d.lever}}</span>
                                        <span class="gtag" [class.g-grounded]="d.grounding === 'grounded'" [class.g-ind]="d.grounding === 'indicative'">{{d.grounding}}</span>
                                        <h4 class="with-context">{{d.title}} @if (showLinks) { @if (decisionContext(d); as request) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="request" [name]="d.title"></app-context-graph-button>} }</h4>
                                        <div class="prio">
                                            <div class="plabel"><span>{{d.gate ? 'Priority' : 'Priority score'}}</span><span class="pscore">{{d.gate ? 'Gate · do first' : d.priority + ' / 100'}}</span></div>
                                            <div class="prbar"><i [style.width.%]="d.priority"></i></div>
                                        </div>
                                    </div>
                                </div>
                                <div class="dsignal"><b>Signal:</b> {{d.signal}}</div>
                                <div class="drat">{{d.rationale}}</div>
                                @if (d.nodes.length) {
                                    <div class="dnodes">
                                        <div class="lbl">Nodes involved</div>
                                        <div class="nodechips">
                                            @for (n of d.nodes; track $index) {
                                                <span class="nodechip"><span class="nt" [class]="'nt nt-' + n.kind">{{n.kind}}</span>{{n.name}}</span>
                                            }
                                        </div>
                                    </div>
                                }
                                <div class="dnodes">
                                    <div class="lbl">Expected movement</div>
                                    <div class="dimpact">
                                        @for (i of d.impacts; track $index) {
                                            <span class="impchip"><span class="d">{{i.dimension}}</span> {{i.movement}}</span>
                                        }
                                    </div>
                                </div>
                            </div>
                            <div class="foot2">
                                <span class="ef">Effort <b>{{d.effort}}</b></span>
                                <span class="ef">Confidence <b>{{d.confidence}}</b></span>
                                <span class="dep">Depends on <b>{{d.dependsOn}}</b></span>
                                @if (state === 'future') {
                                    @switch (status(d)) {
                                        @case ('resolved') { <span class="status status-resolved">&#10003; Resolved in future state</span> }
                                        @case ('improved') { <span class="status status-improved">Improved in future state</span> }
                                        @case ('open') { <span class="status">Still open</span> }
                                        @case ('untracked') { <span class="status status-untracked" matTooltip="It addresses no specific signal in the model, so the future state can't confirm it">Not tracked</span> }
                                        @default { <span class="status status-untracked">Guardrail check</span> }
                                    }
                                }
                            </div>
                        </div>
                    }
                </div>
            }@else {
                <div class="card" data-pdf-block data-tour="decisions">No decisions stand out: there's no capacity overload, friction, single point of failure or heavy manual work in the models shown.</div>
            }

            <section data-pdf-block [attr.data-tour]="scorecard.dataGaps.length ? 'data-gaps' : null">
            @if (scorecard.dataGaps.length) {
                <div class="sectlabel">Data gaps &middot; what the model doesn't carry yet</div>
                <ul class="gaps">
                    @for (g of scorecard.dataGapDetails; track $index) {
                        @if (g.items.length && !exporting && !readOnly) {
                        <!-- the items it is about show on hovering over it or moving to it, each opening where it is set up -->
                        <li class="gap-with-items" tabindex="0" [attr.aria-describedby]="'gap-items-' + $index" data-testid="data-gap">
                            {{g.text}}
                            <div class="gap-items" role="tooltip" [id]="'gap-items-' + $index">
                                <div class="gap-items-title">{{g.items.length === 1 ? 'The one' : 'The ' + g.items.length}} it's about &middot; open one to fix it</div>
                                <ul>
                                    @for (i of g.items; track i.key) {
                                    <li><button type="button" class="gap-item" (click)="editInModel(i.modelKey, i.key)" [title]="'Open ' + i.name + (i.model ? ' in ' + i.model : ' in the organisation model')">{{i.name}}@if (i.model) {<span> &middot; {{i.model}}</span>}</button></li>
                                    }
                                </ul>
                            </div>
                        </li>
                        } @else {
                        <li>{{g.text}}</li>
                        }
                    }
                </ul>
            }
            <div class="foot">Generated from the models shown in the analysis graph for {{workspaceName}}. Change the model filter to score a different set of models.</div>
            </section>
        </div>
    }@else {
        <div class="wrap"><div class="card">The scorecard appears once the graph has loaded.</div></div>
    }
</div>

<ng-template #frictionLens>
    @if (scorecard.frictionRows.length) {
        <table>
            <tr><th>Output</th><th>Model</th><th>State</th><th class="num">Items / yr</th><th class="num">Hours</th><th class="num">Cost</th>@if (canCorrect && !exporting) {<th>Correct</th>}</tr>
            @for (r of scorecard.frictionRows; track $index) {
                <tr><td><span class="with-context">{{r.name}} @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: 'output', key: r.key, name: r.name}}" [name]="r.name"></app-context-graph-button><app-edit-in-model [name]="r.name" (edit)="editInModel(r.modelKey, r.key)"></app-edit-in-model>}</span></td><td>{{r.model}}</td><td><span class="tag" [class.tag-rework]="r.state === 'Dead end'" [class.tag-extra]="r.state === 'Unvalued'">{{r.state}}</span></td>
                    <td class="num">{{r.items}}</td><td class="num">{{r.hours}}</td><td class="num">{{r.cost}}</td>
                    @if (canCorrect && !exporting) {<td><app-value-fix [name]="r.name" label="Set its value" [disabled]="correcting" (set)="setOutputValue(r, $event)"></app-value-fix></td>}</tr>
            }
        </table>
    }
    @if (scorecard.detourRows.length) {
        <h4 class="lens-sub">Exception work</h4>
        <table data-testid="detour-rows">
            <tr><th>Items leave at</th><th>Through</th><th>Return at</th><th>Model</th><th class="num">Items / yr</th><th class="num">Hours</th><th class="num">Cost</th><th>Read</th></tr>
            @for (r of scorecard.detourRows; track r.key) {
                <tr><td>{{r.leavesAt}}{{r.share ? ' (' + r.share + '% of items)' : ''}}</td><td><span class="with-context">{{r.steps.join(', ')}} @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: detourSubject(r.key, r.steps.join(', '))}" [name]="'the detour through ' + r.steps.join(', ')"></app-context-graph-button><app-edit-in-model [name]="r.steps[0]" (edit)="editInModel(r.modelKey, r.stepKeys[0])"></app-edit-in-model>}</span></td><td>{{r.returnsAt}}</td><td>{{r.model}}</td>
                    <td class="num">{{r.items}}</td><td class="num">{{r.hours}}</td><td class="num">{{r.cost}}</td>
                    <td><span class="tag" [class.tag-good]="r.confirmed" [class.tag-extra]="!r.confirmed">{{r.confirmed ? 'Confirmed' : 'Read by LINQ'}}</span>
                        @if (canCorrect && !exporting) {
                            <span class="fixes">
                                @if (!r.confirmed) {<button type="button" class="link-button" (click)="setWorkType(r, WORK_TYPE.EXCEPTION)" [disabled]="correcting" title="Say the work on it is exception work">Confirm</button>}
                                <button type="button" class="link-button" (click)="setWorkType(r, WORK_TYPE.VALUE_ADDING)" [disabled]="correcting" title="Say the work on it adds value, so it isn't friction and earns a share of the value">Value adding</button>
                                <button type="button" class="link-button" (click)="setWorkType(r, WORK_TYPE.NECESSARY)" [disabled]="correcting" title="Say the work on it is necessary, such as an approval: the cost of doing business, not friction">Necessary</button>
                            </span>
                        }
                    </td></tr>
            }
        </table>
    }
    @if (bridgeRows.length) {
        <h4 class="lens-sub">Manual bridges between systems</h4>
        <table data-testid="friction-bridges">
            <tr><th>What</th><th>Model</th><th class="num">Friction hrs / yr</th><th>Also</th></tr>
            @for (r of bridgeRows; track r.key) {
                <tr><td><span class="with-context"><strong>{{r.title}}</strong> @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: r.context.kind, key: r.context.key, name: r.title}}" [name]="r.title"></app-context-graph-button><app-edit-in-model name="the first step" (edit)="editInModel(r.context.modelKey, r.context.key)"></app-edit-in-model>}</span><div class="risk-detail">{{r.detail}}</div>
                    @if (canCorrect && !exporting && r.systems.length) {<div class="fix-line"><span class="fix-label">The systems' kinds:</span><app-system-kind-fix [systems]="systemChoices(r.systems)" [disabled]="correcting" (changed)="setSystemKind($event)"></app-system-kind-fix></div>}</td><td>{{r.model}}</td><td class="num">{{r.frictionHours | number:'1.0-0'}}</td>
                    <td><span class="also">a risk too, under Risk</span></td></tr>
            }
        </table>
    }
    @if (scorecard.wasteRows.length) {
        <h4 class="lens-sub">Not value adding</h4>
        <table data-testid="waste-rows">
            <tr><th>Step</th><th>Model</th><th class="num">Items / yr</th><th class="num">Hours</th><th class="num">Cost</th>@if (canCorrect && !exporting) {<th></th>}</tr>
            @for (r of scorecard.wasteRows; track r.key) {
                <tr><td><span class="with-context">{{r.name}} @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: 'action', key: r.key, name: r.name}}" [name]="r.name"></app-context-graph-button><app-edit-in-model [name]="r.name" (edit)="editInModel(r.modelKey, r.key)"></app-edit-in-model>}</span></td>
                    <td>{{r.model}}</td><td class="num">{{r.items}}</td><td class="num">{{r.hours}}</td><td class="num">{{r.cost}}</td>
                    @if (canCorrect && !exporting) {
                        <td><span class="fixes">
                            <button type="button" class="link-button" (click)="setStepWorkType(r.modelKey, r.key, WORK_TYPE.VALUE_ADDING)" [disabled]="correcting" title="Say the work adds value, so it isn't friction and earns a share of the value">Value adding</button>
                            <button type="button" class="link-button" (click)="setStepWorkType(r.modelKey, r.key, WORK_TYPE.NECESSARY)" [disabled]="correcting" title="Say the work is necessary, such as a control: the cost of doing business, not friction">Necessary</button>
                        </span></td>
                    }</tr>
            }
        </table>
    }
    @if (scorecard.rekeyRows.length) {
        <h4 class="lens-sub">Re-keying</h4>
        <table data-testid="rekey-rows">
            <tr><th>Step</th><th>Types in</th><th>From</th><th>Into</th><th>Model</th><th class="num">Items / yr</th><th class="num">Hours</th><th class="num">Cost</th>@if (canCorrect && !exporting) {<th></th>}</tr>
            @for (r of scorecard.rekeyRows; track r.key) {
                <tr><td><span class="with-context">{{r.step}} @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: 'action', key: r.stepKey, name: r.step}}" [name]="r.step"></app-context-graph-button><app-edit-in-model [name]="r.step" (edit)="editInModel(r.modelKey, r.stepKey)"></app-edit-in-model>}</span></td>
                    <td>{{r.information}}</td><td>{{r.medium}}</td><td>{{r.systems}}</td><td>{{r.model}}</td>
                    <td class="num">{{r.items | number:'1.0-0'}}</td><td class="num">{{r.hours | number:'1.0-0'}}</td><td class="num">{{money(r.cost)}}</td>
                    @if (canCorrect && !exporting) {
                        <td><span class="fixes"><button type="button" class="link-button" (click)="setStepWorkType(r.modelKey, r.stepKey, WORK_TYPE.NOT_VALUE_ADDING)" [disabled]="correcting" title="LINQ reads re-keying as waste: say the whole step doesn't add value, so it earns no share of the value">Not value adding</button></span></td>
                    }</tr>
            }
        </table>
    }
    <div class="note">{{scorecard.frictionNote}}</div>
</ng-template>

<ng-template #riskLens>
    @if (scorecard.riskRows.length) {
        <table>
            <tr><th>Component</th><th>Why it matters</th><th>Dependency</th><th class="num">Load</th><th class="num" title="The revenue lost if it stopped: the work only it does has no other route to the outputs">Revenue at risk</th><th class="num" title="Its share of the revenue its work creates. These add up across roles, and across systems">Revenue it creates</th></tr>
            @for (r of scorecard.riskRows; track $index) {
                <tr><td><span class="with-context"><span class="nt" [class.nt-role]="r.kind === 'Role'" [class.nt-system]="r.kind === 'System'">{{r.kind}}</span> {{r.name}} @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: r.kind === 'Role' ? 'role' : 'system', key: r.key, name: r.name}}" [name]="r.name"></app-context-graph-button><app-edit-in-model [name]="r.name" (edit)="editInModel(ORGANISATION_MODEL_KEY, r.key)"></app-edit-in-model>}</span></td><td>{{r.why}}</td><td>{{r.dependency}}</td>
                    <td class="num">{{r.load}}</td><td class="num">{{r.revenueAtRisk}}</td><td class="num">{{r.revenueCreated}}</td></tr>
            }
        </table>
    }
    <div class="note">{{scorecard.riskNote}}</div>
    @if (scorecard.informationRisks.length) {
        <h4 class="lens-sub">Information and system risk</h4>
        <table data-testid="information-risks">
            <tr><th>What</th><th>Model</th><th class="num">Hours / yr</th><th>Read</th></tr>
            @for (r of scorecard.informationRisks; track r.key) {
                <tr><td><span class="with-context"><strong>{{r.title}}</strong> @if (showLinks) {<app-context-graph-button [organisation]="organisation" [scope]="contextScope" [source]="contextSource" [request]="{subject: {kind: r.context.kind, key: r.context.key, name: r.title}}" [name]="r.title"></app-context-graph-button><app-edit-in-model [name]="r.context.kind === 'system' ? 'the system' : r.context.kind === 'output' ? 'the output' : 'the step'" (edit)="editInModel(r.context.modelKey, r.context.key)"></app-edit-in-model>}</span><div class="risk-detail">{{r.detail}}</div>
                    @if (canCorrect && !exporting) {
                        @if (r.information; as info) {<div class="fix-line"><span class="fix-label">Held as:</span><app-held-as-fix [type]="info.type" [read]="info.read" [disabled]="correcting" (changed)="setHeldAs(info, $event)"></app-held-as-fix></div>}
                        @if (r.systems.length) {<div class="fix-line"><span class="fix-label">{{r.systems.length === 1 ? 'The system\'s kind:' : 'The systems\' kinds:'}}</span><app-system-kind-fix [systems]="systemChoices(r.systems)" [disabled]="correcting" (changed)="setSystemKind($event)"></app-system-kind-fix></div>}
                    }</td><td>{{r.model}}</td><td class="num">{{r.hours | number:'1.0-0'}}</td>
                    <td><span class="tag" [class.tag-rework]="r.severity === 'high'" [class.tag-extra]="r.severity !== 'high'">{{r.kind === 'bridge' ? 'Manual bridge' : r.kind === 'copy' ? 'Unofficial copy' : 'Local tool'}}</span>@if (r.alsoFriction) {<span class="also">also friction</span>}@if (r.systemsSuggested) {<span class="also" title="A system's kind here is LINQ's read of its name: confirm it on the system">kind read by LINQ</span>}</td></tr>
            }
        </table>
    }
    <div class="note">{{scorecard.informationRiskNote}}</div>
</ng-template>

<ng-template #informationLens>
    @if (!exporting && !readOnly && scorecard.informationRows.length) {
        <div class="decision-source value-suggestions" role="status" data-testid="value-suggestions">
            @switch (valueState) {
                @case ('loading') {
                    <span class="source-tag">Suggesting values with AI&hellip;</span>
                }
                @case ('ready') {
                    <span class="source-tag ai">AI suggestions</span>
                    <span>A value per item (0 to 100) for each output, with AI's reasoning.{{canApplyValues ? ' Use one to set it on the output.' : ''}}</span>
                    @if (canApplyValues && pendingSuggestions.length) {
                        <button type="button" class="link-button" (click)="applyAllSuggestions()" [disabled]="applyingValues" data-testid="use-all-suggestions">Use all ({{pendingSuggestions.length}})</button>
                    }
                    <button type="button" class="link-button" (click)="suggestValues(true)" [disabled]="applyingValues">Regenerate</button>
                }
                @case ('failed') {
                    <span class="source-tag">AI</span>
                    <span>Values couldn't be suggested.</span>
                    <button type="button" class="link-button" (click)="suggestValues(true)">Try again</button>
                }
                @default {
                    <span>LINQ derives each output's value from how it is used.{{canApplyValues ? ' Use it, or ask AI to weigh the evidence.' : ''}}</span>
                    <button type="button" class="link-button" (click)="suggestValues()" data-testid="suggest-values">Suggest values with AI</button>
                }
            }
            @if (applyingValues) {
                <span>Saving the values and rescoring&hellip;</span>
            }
            @if (valueError) {
                <div class="error" role="alert">{{valueError}}</div>
            }
        </div>
    }
    <table>
        <tr><th>Output</th><th>Model</th><th class="num">Value</th><th class="num">Derived</th><th>Held as</th>@if (valueSuggestions) {<th class="num">AI suggests</th>}<th class="num">Revenue</th><th>Reuse</th><th class="num">Cost to produce</th><th>State</th></tr>
        @for (r of scorecard.informationRows; track r.key) {
            <tr><td><span class="with-context">{{r.name}}@if (r.asset) {<span class="asset-tag" [title]="'A state of the information asset ' + r.asset">{{r.asset}}</span>} @if (showLinks) {<app-edit-in-model [name]="r.name" (edit)="editInModel(r.modelKey, r.key)"></app-edit-in-model>}</span></td><td>{{r.model}}</td><td class="num">{{r.value}}</td>
                <td class="num">{{r.derived}}
                    @if (canApplyValues && !exporting && !r.joinedOut && r.derivedValue > 0 && r.userValue !== r.derivedValue) {
                        <button type="button" class="link-button" (click)="useDerivedValue(r)" [disabled]="applyingValues" title="Set the derived value as the output's value per item">Use</button>
                    }
                </td>
                <td [class.held-outside]="r.heldOutside">
                    @if (canCorrect && !exporting) {<app-held-as-fix [type]="r.type" [read]="r.typeRead" [name]="r.name" [disabled]="correcting" (changed)="setHeldAs(r, $event)"></app-held-as-fix>}@else {{{r.held}}}
                </td>
                @if (valueSuggestions) {
                    <td class="num">
                        @if (suggestionFor(r); as s) {
                            <span [title]="s.rationale">{{s.value}} / 100</span>
                            @if (canApplyValues && !exporting && s.value !== r.userValue) {
                                <button type="button" class="link-button" (click)="applySuggestion(r)" [disabled]="applyingValues" title="Set AI's suggestion as the output's value per item">Use</button>
                            }
                        }@else {
                            -
                        }
                    </td>
                }
                <td class="num">{{r.revenue}}</td><td>{{r.reuse}}</td><td class="num">{{r.costToProduce}}</td>
                <td><span class="tag" [class.tag-good]="r.state === 'Revenue bearing' || r.state === 'Valued'" [class.tag-extra]="r.state === 'In use'" [class.tag-rework]="r.state === 'Dead end'">{{r.state}}</span></td></tr>
            @if (valueSuggestions && suggestionFor(r); as s) {
                <tr class="rationale"><td colspan="10">{{s.rationale}}</td></tr>
            }
        }
    </table>
    <div class="note">{{scorecard.informationNote}}</div>
</ng-template>

<ng-template #panel let-p let-section="section">
    <div class="card">
        <h3>{{p.title}} <span class="gtag" [class.g-grounded]="p.grounding === 'grounded'" [class.g-ind]="p.grounding === 'indicative'">{{p.grounding}}</span></h3>
        <div class="sub">{{p.sub}}</div>
        @for (l of p.lines; track $index) {
            <div class="metric" [class.group]="l.kind === 'group'" [class.item]="l.kind === 'item'" [class.total]="l.kind === 'total'"><span class="k">{{l.label}}</span><span class="v">{{l.value}}<ng-container *ngTemplateOutlet="chip; context: {$implicit: panelChange(section, p.title, l.label, l.measure)}"></ng-container></span></div>
        }
        <div class="note">{{p.note}}</div>
    </div>
</ng-template>

<ng-template #chip let-c>
    @if (c) {
        <span class="chg" [class.chg-good]="c.tone === 'good'" [class.chg-bad]="c.tone === 'bad'" [class.chg-flat]="c.tone === 'flat'">{{c.text}}</span>
    }
</ng-template>
